import { confirmDialog } from "@/lib/confirm";
import React, { useEffect, useState } from "react";
import { Download, Copy, Monitor, XCircle, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { offlineLicenseApi, usersApi } from "../lib/api";

const hata = (e) => toast.error(e?.response?.data?.detail || "İşlem başarısız");
const tarih = (s) => (s ? new Date(s).toLocaleString("tr-TR") : "—");
const yilSonra = () => new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10);

const indir = (kod, ad) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([kod], { type: "text/plain" }));
  a.download = ad;
  a.click();
  URL.revokeObjectURL(a.href);
};
const kopyala = (kod) => navigator.clipboard.writeText(kod).then(() => toast.success("Lisans kodu panoya kopyalandı"), () => toast.error("Kopyalanamadı"));

const KullaniciSec = ({ secili, onSec }) => {
  const [q, setQ] = useState("");
  const [sonuc, setSonuc] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setSonuc([]); return undefined; }
    const t = setTimeout(() => usersApi.search(q.trim()).then((r) => setSonuc(Array.isArray(r) ? r : r.items || [])).catch(() => setSonuc([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  if (secili) return (
    <button type="button" onClick={() => onSec(null)} className="text-xs font-mono text-cyan-300 border border-cyan-400/40 rounded px-2 py-1" data-testid="offline-license-user-selected">
      {secili.username} ✕
    </button>
  );
  return (
    <div className="relative">
      <div className="flex items-center gap-1 border border-white/10 rounded px-2">
        <Search className="h-3 w-3 opacity-60" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kullanıcı ara…" className="bg-transparent text-xs py-1 outline-none w-40" data-testid="offline-license-user-search" />
      </div>
      {sonuc.length > 0 && (
        <div className="absolute z-20 mt-1 w-56 bg-[#0b1220] border border-white/10 rounded max-h-48 overflow-auto">
          {sonuc.map((u) => (
            <button type="button" key={u.id} onClick={() => { onSec(u); setQ(""); }} className="block w-full text-left px-2 py-1 text-xs hover:bg-white/5" data-testid={`offline-license-user-option-${u.username}`}>
              {u.username} {u.full_name ? <span className="opacity-60">· {u.full_name}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const LisansSatiri = ({ l, onIptal }) => (
  <div className="border border-white/10 rounded p-2 text-xs font-mono" data-testid={`offline-license-row-${l.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-cyan-200">{l.username}</span>
      <span className="opacity-70">{l.tur}</span>
      <span className="opacity-70">bitiş {String(l.bitis).slice(0, 10)}</span>
      <span className="opacity-70">{(l.etkinlestirmeler || []).length}/{l.max_cihaz} cihaz</span>
      <span className="opacity-50">{l.kaynak === "otomatik" ? "otomatik" : "yönetici"}</span>
      {l.iptal ? <span className="text-rose-300" data-testid={`offline-license-revoked-${l.id}`}>İPTAL</span> : (
        <>
          <button type="button" onClick={() => offlineLicenseApi.code(l.id).then((r) => indir(r.kod, r.dosya_adi)).catch(hata)} className="text-cyan-300 flex items-center gap-0.5" data-testid={`offline-license-download-${l.id}`}><Download className="h-3 w-3" />Dosya</button>
          <button type="button" onClick={() => offlineLicenseApi.code(l.id).then((r) => kopyala(r.kod)).catch(hata)} className="text-cyan-300 flex items-center gap-0.5" data-testid={`offline-license-copy-${l.id}`}><Copy className="h-3 w-3" />Kod</button>
          <button type="button" onClick={() => onIptal(l)} className="text-rose-300 flex items-center gap-0.5" data-testid={`offline-license-revoke-${l.id}`}><XCircle className="h-3 w-3" />İptal et</button>
        </>
      )}
    </div>
    {(l.etkinlestirmeler || []).map((e) => (
      <div key={e.cihaz_id} className="flex items-center gap-1 opacity-70 mt-1 pl-2" data-testid={`offline-license-device-${l.id}`}>
        <Monitor className="h-3 w-3" />{e.cihaz_adi || e.cihaz_id.slice(0, 8)} · son görülme {tarih(e.son)}
      </div>
    ))}
  </div>
);

export const OfflineLicensePanel = () => {
  const [liste, setListe] = useState([]);
  const [kisi, setKisi] = useState(null);
  const [bitis, setBitis] = useState(yilSonra());
  const [max, setMax] = useState(1);
  const [son, setSon] = useState(null);
  const [yuk, setYuk] = useState(false);
  const [anahtar, setAnahtar] = useState(null);
  const yenile = () => offlineLicenseApi.list().then(setListe).catch(hata);
  useEffect(() => { yenile(); offlineLicenseApi.keyStatus().then(setAnahtar).catch(() => setAnahtar(null)); }, []);
  const olustur = async () => {
    if (!kisi) { toast.error("Önce kullanıcı seçin"); return; }
    setYuk(true);
    try {
      setSon(await offlineLicenseApi.create({ user_id: kisi.id, bitis, max_cihaz: Number(max) || 1 }));
      toast.success("Çevrimdışı lisans oluşturuldu");
      yenile();
    } catch (e) { hata(e); } finally { setYuk(false); }
  };
  const iptal = async (l) => {
    if (!(await confirmDialog({ message: `${l.username} kullanıcısının çevrimdışı lisansı iptal edilsin mi? Masaüstü bir sonraki çevrimiçi kontrolde salt okunur olur.`, danger: true }))) return;
    offlineLicenseApi.revoke(l.id).then(() => { toast.success("Lisans iptal edildi"); yenile(); }).catch(hata);
  };
  return (
    <div className="space-y-2 border-t border-white/10 pt-3 mt-4" data-testid="offline-license-panel">
      <div className="text-sm font-semibold text-cyan-200">Masaüstü — çevrimdışı lisanslar</div>
      {anahtar && (
        <div
          className={`text-xs font-mono ${{ dogru: "text-emerald-300", eksik: "text-amber-300", hatali: "text-rose-300" }[anahtar.durum]}`}
          title={anahtar.mesaj}
          data-testid="offline-license-key-status"
          data-durum={anahtar.durum}
        >
          Lisans anahtarı: {{ dogru: "doğru ✓", eksik: "eksik", hatali: "hatalı" }[anahtar.durum]}
          {anahtar.durum !== "dogru" && <span className="opacity-80"> — {anahtar.mesaj}</span>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <KullaniciSec secili={kisi} onSec={setKisi} />
        <input type="date" value={bitis} onChange={(e) => setBitis(e.target.value)} className="bg-transparent border border-white/10 rounded px-2 py-1 text-xs" data-testid="offline-license-expiry" />
        <input type="number" min={1} max={50} value={max} onChange={(e) => setMax(e.target.value)} className="bg-transparent border border-white/10 rounded px-2 py-1 text-xs w-16" title="En çok cihaz" data-testid="offline-license-max-devices" />
        <button type="button" onClick={olustur} disabled={yuk} className="px-2 py-1 text-xs border border-cyan-400/50 text-cyan-300 rounded flex items-center gap-1" data-testid="offline-license-create">
          {yuk && <Loader2 className="h-3 w-3 animate-spin" />}Çevrimdışı lisans oluştur
        </button>
      </div>
      {son && (
        <div className="flex items-center gap-2 text-xs" data-testid="offline-license-created">
          <span className="font-mono opacity-80">{son.dosya_adi}</span>
          <button type="button" onClick={() => indir(son.kod, son.dosya_adi)} className="px-2 py-1 border border-emerald-400/50 text-emerald-300 rounded flex items-center gap-1" data-testid="offline-license-download-file"><Download className="h-3 w-3" />Lisans dosyası indir</button>
          <button type="button" onClick={() => kopyala(son.kod)} className="px-2 py-1 border border-emerald-400/50 text-emerald-300 rounded flex items-center gap-1" data-testid="offline-license-copy-code"><Copy className="h-3 w-3" />Kodu kopyala</button>
        </div>
      )}
      <div className="space-y-1" data-testid="offline-license-list">
        {liste.map((l) => <LisansSatiri key={l.id} l={l} onIptal={iptal} />)}
        {liste.length === 0 && <div className="text-xs opacity-60">Henüz çevrimdışı lisans yok.</div>}
      </div>
    </div>
  );
};
